
import {defineComponent} from "vue";
import {
  CodeDescriptionRestDto,
  MaterialSymbolRestDto,
  TitleMetaDataRestDto
} from "@/api/pharma-cms-content-block/models";
import PageLinkPicker from "@/components/UI/PageLinkPicker.vue";
import {PageLinkPayload} from "@/models/payload/PageLinkPayload";
import {PageLinkTypeEnum} from "@/models/enum/PageLinkTypeEnum";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {LinkTargetEnum} from "@/models/enum/LinkTargetEnum";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import {SymbolTargetEnum} from "@/api/enums/symbol-target-enum";
import Subtitle from "@/components/UI/Subtitle.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputText from "@/components/UI/InputText.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import PreviewSymbol from "@/components/UI/PreviewSymbol.vue";
import {
  TextWithSymbolContentRestDtoModel
} from "@/models/api/pharma-cms-content-block/TextWithSymbolContentRestDtoModel";
import {ButtonRestDtoModel} from "@/models/api/pharma-cms-content-block/ButtonRestDtoModel";
import {ButtonTargetEnum} from "@/api/enums/button-target-enum";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import MaterialSymbolInputComponent from "@/components/UI/MaterialSymbolInputComponent.vue";
import {TitleMetaDataRestDtoModel} from "@/models/api/pharma-cms-content-block/TitleMetaDataRestDtoModel";
import AdvancedPanelSwitcher from "@/components/UI/AdvancedPanelSwitcher.vue";
import AdvancedPanel from "@/components/UI/AdvancedPanel.vue";
import TitleMetaInputComponent from "@/components/UI/TitleMetaInputComponent.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "CbTextWithSymbol",
  extends: CbStructureMixin,
  components: {
    TitleMetaInputComponent,
    AdvancedPanel,
    AdvancedPanelSwitcher,
    MaterialSymbolInputComponent,
    InputTextEditor,
    PreviewSymbol,
    Subtitle, InputCheckboxBoolean, InputText, InputSelect, PageLinkPicker, BaseSpinner
  },
  data: () => ({
    contentBlock: TextWithSymbolContentRestDtoModel.createWithDefaults(),
    pageLinkSymbolPayload: PageLinkPayload.createWithDefaults(),
    pageLinkButtonPayload: PageLinkPayload.createWithDefaults(),
    useButton: false as boolean,
    removedButton: ButtonRestDtoModel.createWithDefaults(),
    isAdvancedTitleMeta: false,
    enumButtonLayoutCode: [] as CodeDescriptionRestDto[],
    enumLayoutCode: [] as CodeDescriptionRestDto[],
    enumObjectHorizontalPosition: [] as CodeDescriptionRestDto[],
    enumTitleSizeCode: [] as CodeDescriptionRestDto[],
    enumVerticalPosition: [] as CodeDescriptionRestDto[],
  }),
  mounted() {
    this.initialize();
  },
  watch: {
    contentBlock() {
      this.buildPageLinkPayload();

      if (this.contentBlock.button) {
        this.useButton = true;
        this.removedButton = this.contentBlock.button;
      } else {
        this.useButton = false;
        this.contentBlock.button = undefined;
        this.removedButton = ButtonRestDtoModel.createWithDefaults();
      }
    },
    useButton(newVal) {
      if (newVal === true) {
        this.contentBlock.button = this.removedButton;

        this.buildPageLinkButtonPayload();
      } else {
        if (this.contentBlock.button !== undefined) {
          this.removedButton = this.contentBlock.button as ButtonRestDtoModel;
        }
        this.contentBlock.button = undefined
      }
    }
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<TextWithSymbolContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findTextWithSymbol(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createTextWithSymbol(payload.cb as TextWithSymbolContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveTextWithSymbol(payload.cb as TextWithSymbolContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      [
        this.enumButtonLayoutCode,
        this.enumLayoutCode,
        this.enumObjectHorizontalPosition,
        this.enumTitleSizeCode,
        this.enumVerticalPosition
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BUTTON_LAYOUT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TEXT_WITH_IMAGE_LAYOUT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_HORIZONTAL_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TITLE_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_VERTICAL_POSITION, DpModuleEnum.CMS)
      ]);
    },
    afterReloadContent() {
      if (this.contentBlock.title_meta) {
        this.isAdvancedTitleMeta = true;
      }
      if (this.contentBlock.button) {
        this.useButton = true;
        this.removedButton = this.contentBlock.button;
      }
    },
    toggleAdvancedTitleMeta(): void {
      if (!this.isAdvancedTitleMeta && (this.contentBlock.title_meta === null || this.contentBlock.title_meta === undefined)) {
        this.contentBlock.title_meta = TitleMetaDataRestDtoModel.createWithDefaults();
      }
      this.isAdvancedTitleMeta = !this.isAdvancedTitleMeta;
    },
    onTitleMetaUpdate(payload: TitleMetaDataRestDto): void {
      this.contentBlock.title_meta = payload;
      this.onChange();
    },
    async beforeSubmitData(): Promise<void> {
      if (this.contentBlock !== null && this.contentBlock.title_meta !== null && this.contentBlock.title_meta !== undefined
        && this.contentBlock.title_meta.font_size_in_rem === null
        && this.contentBlock.title_meta.font_weight_code === null
        && this.contentBlock.title_meta.text_horizontal_position_code === null
        && this.contentBlock.title_meta.text_color_code === null
        && this.contentBlock.title_meta.text_color === null) {
        this.contentBlock.title_meta = null;
      }
    },
    getDefaultCbModel(): TextWithSymbolContentRestDtoModel {
      return TextWithSymbolContentRestDtoModel.createWithDefaults();
    },
    buildPageLinkPayload(): void {
      this.buildPageLinkButtonPayload();
      this.buildPageLinkSymbolPayload();
    },
    buildPageLinkButtonPayload(): void {
      const externalUrlButton = this.contentBlock.button?.button_url?.nl ?? null;
      const pageIdButton = this.contentBlock.button?.button_page_id?.nl ?? null;
      const targetButton = this.contentBlock.button?.button_target ?? LinkTargetEnum.SELF;

      this.pageLinkButtonPayload = new PageLinkPayload(externalUrlButton, pageIdButton, targetButton.toString() as LinkTargetEnum);
    },
    buildPageLinkSymbolPayload(): void {
      const externalUrlSymbol = this.contentBlock.symbol.symbol_url?.nl ?? null;
      const pageIdSymbol = this.contentBlock.symbol.symbol_page_id?.nl ?? null;
      const targetSymbol = this.contentBlock.symbol.symbol_target ?? LinkTargetEnum.SELF;

      this.pageLinkSymbolPayload = new PageLinkPayload(externalUrlSymbol, pageIdSymbol, targetSymbol.toString() as LinkTargetEnum);
    },
    onMaterialSymbolUpdate(payload: MaterialSymbolRestDto): void {
      this.contentBlock.symbol.material_symbol = payload;
      this.onChange();
    },
    onCopyCpAbstractChanged(): void {
      const title = this.contentBlock.title?.nl ?? null;
      const content = this.contentBlock.content?.nl ?? null;
      this.copyCpAbstractPayload = new CopyCpAbstractPayload(content, null, title);

      this.$emit('abstractContentChanged', this.copyCpAbstractPayload);
    },
    onPageLinkPickerSymbolChanged(payload: PageLinkPayload): void {
      if (payload.type === PageLinkTypeEnum.NONE) {
        this.contentBlock.symbol.symbol_url = undefined;
        this.contentBlock.symbol.symbol_page_id = undefined;
        this.contentBlock.symbol.symbol_target = undefined;
      } else if (payload.type === PageLinkTypeEnum.URL) {
        this.contentBlock.symbol.symbol_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
        this.contentBlock.symbol.symbol_page_id = undefined;
        this.contentBlock.symbol.symbol_target = payload.target.toString() as SymbolTargetEnum;
      } else if (payload.type === PageLinkTypeEnum.PAGE) {
        this.contentBlock.symbol.symbol_url = undefined;
        this.contentBlock.symbol.symbol_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
        this.contentBlock.symbol.symbol_target = payload.target.toString() as SymbolTargetEnum;
      }

      this.onChange();
    },
    onPageLinkPickerButtonChanged(payload: PageLinkPayload) {
      if (this.contentBlock.button) {
        if (payload.type === PageLinkTypeEnum.NONE) {
          this.contentBlock.button.button_url = MultilingualRestDtoModel.createWithDefaults();
          this.contentBlock.button.button_page_id = undefined;
          this.contentBlock.button.button_target = ButtonTargetEnum.SELF;
        } else if (payload.type === PageLinkTypeEnum.URL) {
          this.contentBlock.button.button_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
          this.contentBlock.button.button_page_id = undefined;
          this.contentBlock.button.button_target = payload.target.toString() as ButtonTargetEnum;
        } else if (payload.type === PageLinkTypeEnum.PAGE) {
          this.contentBlock.button.button_url = MultilingualRestDtoModel.createWithDefaults();
          this.contentBlock.button.button_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
          this.contentBlock.button.button_target = payload.target.toString() as ButtonTargetEnum;
        }
      }

      this.onChange();
    },
    pasteCbMetadata(): void {
      // Reassigning contentBlock (instead of mutating it) is what fires the contentBlock watcher.
      const cbClone = TextWithSymbolContentRestDtoModel.cloneFrom(this.contentBlock);

      cbClone.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as TextWithSymbolContentRestDtoModel);
      this.contentBlock = cbClone;

      this.buildPageLinkPayload();
    },
  }
});
